// /sports/ball-machine — the TENANT's read-only view of the rental kiosk
// phone(s) linked to their court (K6 Phase 2). Day / week / month with a
// stepper, four stat tiles + "Payable to HighMark", a settlement panel (last
// billing / current payable / previous), then the rental list with flags.
// Every peso figure that is not the phone's own row value is DERIVED on the
// server with the phone's split rule and labelled so. Nothing here moves money.
import { useCallback, useEffect, useState } from 'react';
import { Target, ChevronLeft, ChevronRight, RefreshCw, Smartphone, FileText } from 'lucide-react';
import { toast } from 'sonner';
import SportsNavBar from '../components/SportsNavBar';
import { mineSummary, mineStatement, ymd, epochDayToYmd, errMsg } from '../kiosk/kioskApi';
import { RED, GREEN, GREY, AMBER, fmtCentavos, fmtMs, fmtAgo, labelStyle } from '../kiosk/KioskDialogs';
import { LiveCard, STOP_LABEL } from '../kiosk/KioskLive';
import { StatementModal } from '../kiosk/KioskStatement';
import { fmtHrMin, fmtBookedPlayed } from '../liveUi';
import { useAutoRefresh } from '../kiosk/useAutoRefresh';

// K8: "Since last settlement" opens the page — what is owed since the court and
// HighMark last settled, today included. Week (Mon–Sun) is gone: on a Monday it
// hid Sunday night's rentals. Rolling views have no stepper.
const PERIODS = [['since_paid', 'Since last settlement'], ['day', 'Day'], ['last7', 'Last 7 days'], ['month', 'Month'], ['all', 'All history']];
const ROLLING = ['since_paid', 'last7', 'all'];
const FLAG_LABEL = { court_comp: 'Your comp', problem_stop: 'Problem stop', late_stop: 'Late stop', outage: 'Outage', pro_rated: 'Pro-rated', void: 'Not charged', voucher: 'Voucher', voucher_released: 'Voucher returned' };
const FLAG_COLOR = { court_comp: AMBER, problem_stop: '#FF2D55', late_stop: AMBER, outage: '#FF2D55', pro_rated: GREY, void: GREY, voucher: '#4FC3F7', voucher_released: GREY };
const card = { background: '#141414', border: '1px solid #1E1E1E', borderRadius: 12, padding: 16 };
const hhmm = (ms) => (ms ? new Date(ms).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '—');
const dmy = (s) => (s ? new Date(`${s}T12:00:00`).toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' }) : '—');
const hours = (minutes) => (minutes ? `${(minutes / 60).toFixed(1)} h` : '0 h');
// 7.0f: the statement ref behind a billing entry — a web collection's id as is, a phone collection as "phone:<row_id>".
const billingRef = (b) => (b.source === 'phone' ? `phone:${b.id}` : b.id);
const statementLink = {
  display: 'inline-flex', alignItems: 'center', gap: 5, background: 'none', border: '1px solid #2A2A2A', borderRadius: 8,
  padding: '4px 9px', color: '#ccc', cursor: 'pointer', fontSize: 11, fontWeight: 700,
};

function Tile({ label, value, color = '#fff', sub, testId }) {
  return (
    <div style={card}>
      <p style={labelStyle}>{label}</p>
      <p data-testid={testId} style={{ fontFamily: 'Oswald, sans-serif', fontSize: 24, fontWeight: 900, color, marginTop: 4 }}>{value}</p>
      {sub && <p style={{ fontSize: 11, color: '#555', marginTop: 2 }}>{sub}</p>}
    </div>
  );
}

function Billing({ title, b, testId, emptyText, onStatement }) {
  return (
    <div style={{ flex: 1, minWidth: 220 }} data-testid={testId}>
      <div className="flex items-center justify-between gap-2">
        <p style={labelStyle}>{title}</p>
        {b && onStatement && (
          <button onClick={() => onStatement(b)} data-testid={`${testId}-statement`} style={statementLink}><FileText className="h-3 w-3" /> Statement</button>
        )}
      </div>
      {!b ? (
        <p style={{ fontSize: 13, color: '#555', marginTop: 6 }}>{emptyText}</p>
      ) : (
        <div style={{ marginTop: 6 }}>
          <p style={{ fontSize: 13, color: '#ccc' }}>
            {dmy(epochDayToYmd(b.period_from_epoch_day))} – {dmy(epochDayToYmd(b.period_to_epoch_day))}
            <span style={{ color: GREY }}> · {b.session_count ?? '—'} rentals · settled {fmtMs(b.collected_at_ms)}</span>
          </p>
          <div className="flex gap-4 mt-2 flex-wrap">
            <div><p style={{ fontSize: 11, color: GREY }}>Machine takings</p><p style={{ fontSize: 16, fontWeight: 700 }}>{fmtCentavos(b.gross_centavos)}</p></div>
            <div><p style={{ fontSize: 11, color: GREY }}>Court kept</p><p style={{ fontSize: 16, fontWeight: 700, color: b.court_centavos < 0 ? AMBER : GREEN }}>{fmtCentavos(b.court_centavos)}</p></div>
            <div><p style={{ fontSize: 11, color: GREY }}>Payable to HighMark</p><p style={{ fontSize: 16, fontWeight: 700 }}>{fmtCentavos(b.owner_centavos)}</p></div>
          </div>
          <p style={{ fontSize: 11, color: '#555', marginTop: 4 }}>
            {b.source === 'web' ? 'Settled on the web' : 'Settled on the machine'}{b.derived ? ' · derived' : ''}{b.conflict ? ' · CONFLICT — under review' : ''}{b.note ? ` · ${b.note}` : ''}
          </p>
        </div>
      )}
    </div>
  );
}

export default function SportsBallMachinePage() {
  const [period, setPeriod] = useState('since_paid');
  const [anchor, setAnchor] = useState(ymd());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statement, setStatement] = useState(null);     // 7.0f: { title, load } for the open statement

  const openStatement = (b) => {
    const ref = billingRef(b);
    setStatement({
      title: `Statement · ${dmy(epochDayToYmd(b.period_from_epoch_day))} – ${dmy(epochDayToYmd(b.period_to_epoch_day))}`,
      load: () => mineStatement(ref, b.kiosk_id),
    });
  };

  const load = useCallback((silent = false) => {
    if (!silent) { setLoading(true); setError(''); }
    return mineSummary({ period, anchor })
      .then(d => { setData(d); setError(''); })
      .catch(e => { if (silent) return; setError(errMsg(e, 'Could not load the ball machine summary')); toast.error(errMsg(e, 'Could not load')); })
      .finally(() => setLoading(false));
  }, [period, anchor]);
  useEffect(() => { load(); }, [load]);
  useAutoRefresh(() => load(true));

  const step = (dir) => {
    if (!data?.period) return;
    setAnchor(dir < 0 ? data.period.prev_anchor : data.period.next_anchor);
  };
  const t = data?.totals;
  const p = data?.payable_to_owner;
  const isFuture = data?.period && data.period.from > ymd();
  const rolling = ROLLING.includes(period);
  const st = data?.settlement;
  const periodLabel = () => {
    if (!data?.period) return '…';
    if (period === 'since_paid') return st?.settled_through ? `${dmy(st.settled_through)} settled · since ${dmy(data.period.from)}` : `never settled · since ${dmy(data.period.from)}`;
    if (period === 'day') return dmy(data.period.from);
    return `${dmy(data.period.from)} – ${dmy(data.period.to)}`;
  };

  return (
    <div style={{ minHeight: '100vh', background: '#0A0A0A', color: '#fff' }}>
      <SportsNavBar />
      <main className="max-w-6xl mx-auto px-5 py-8" data-testid="sports-ball-machine-page">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <span className="h-10 w-10 rounded-lg flex items-center justify-center" style={{ background: RED + '18', color: RED }}>
              <Target className="h-5 w-5" />
            </span>
            <div>
              <h1 style={{ fontFamily: 'Oswald, sans-serif', fontSize: 24, fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.5 }}>Ball Machine</h1>
              <p style={{ fontSize: 12, color: GREY }}>What the rental kiosk reported — read-only. The machine itself is the record.</p>
            </div>
          </div>
          <button onClick={load} data-testid="bm-refresh" title="Refresh"
            style={{ background: 'none', border: '1px solid #2A2A2A', borderRadius: 8, padding: 9, color: GREY, cursor: 'pointer' }}>
            <RefreshCw className="h-4 w-4" />
          </button>
        </div>

        {/* Period chips + stepper */}
        <div className="flex items-center gap-2 mb-5 flex-wrap">
          {PERIODS.map(([k, label]) => (
            <button key={k} onClick={() => { setPeriod(k); setAnchor(ymd()); }} data-testid={`bm-period-${k}`}
              style={{ fontSize: 12, fontWeight: 700, padding: '7px 14px', borderRadius: 999, cursor: 'pointer',
                border: `1px solid ${period === k ? RED : '#2A2A2A'}`, background: period === k ? RED + '18' : 'transparent',
                color: period === k ? RED : GREY }}>{label}</button>
          ))}
          <div className="flex items-center gap-1 ml-2">
            {!rolling && (
              <button onClick={() => step(-1)} data-testid="bm-prev" aria-label="Previous" disabled={!data}
                style={{ background: 'none', border: '1px solid #2A2A2A', borderRadius: 8, padding: 6, color: GREY, cursor: 'pointer' }}><ChevronLeft className="h-4 w-4" /></button>
            )}
            <span data-testid="bm-period-label" style={{ fontSize: 13, color: '#fff', minWidth: 180, textAlign: 'center', fontWeight: 600 }}>{periodLabel()}</span>
            {!rolling && (
              <button onClick={() => step(1)} data-testid="bm-next" aria-label="Next" disabled={!data || isFuture}
                style={{ background: 'none', border: '1px solid #2A2A2A', borderRadius: 8, padding: 6, color: GREY, cursor: 'pointer', opacity: isFuture ? 0.4 : 1 }}><ChevronRight className="h-4 w-4" /></button>
            )}
          </div>
        </div>

        {/* K8: the settlement context — what this view is, in the court's words */}
        {data && data.has_devices && period === 'since_paid' && (
          <div style={{ ...card, marginBottom: 16, borderColor: AMBER + '40' }} data-testid="bm-settlement-context">
            <p style={{ fontSize: 13, lineHeight: 1.6 }}>
              {st?.settled_through
                ? <>Settled up to <b style={{ color: '#fff' }}>{dmy(st.settled_through)}</b>{st.settled_at_ms ? <span style={{ color: GREY }}> ({st.settled_source === 'web' ? 'on the web' : 'on the machine'}, {fmtMs(st.settled_at_ms)})</span> : null}. Since then:</>
                : <>No settlement yet. Everything the machine has recorded:</>}
              {' '}<b style={{ color: '#fff' }}>{t.rentals}</b> rental{t.rentals === 1 ? '' : 's'}{t.running ? ` (+${t.running} running)` : ''}
              {' · '}<b style={{ color: '#fff' }}>{fmtCentavos(t.customers_paid_centavos)}</b> paid by customers
              {' · '}<b style={{ color: AMBER }}>{fmtCentavos(p.owner_centavos)} to be settled to HighMark</b>
              {' · '}your share <b style={{ color: t.court_share_centavos < 0 ? AMBER : GREEN }}>{fmtCentavos(t.court_share_centavos)}</b>
              {t.late_stops > 0 && <span style={{ color: GREY }}> · {t.late_stops} late stop{t.late_stops === 1 ? '' : 's'} ({fmtCentavos(t.late_stop_forgone_centavos)} not charged)</span>}
            </p>
            <p style={{ fontSize: 11, color: '#666', marginTop: 4 }}>A settlement never includes the current day — someone may still rent on it. Today&apos;s rentals show here until the next settlement.</p>
          </div>
        )}
        {data?.period?.capped && <p style={{ fontSize: 12, color: AMBER, marginBottom: 12 }}>Showing the latest {data.period.max_rows} rentals only.</p>}

        {/* K8: machines in use right now, and which phones are online */}
        {data && data.has_devices && (
          <div style={{ marginBottom: 16 }} data-testid="bm-live">
            {data.live.length > 0 && (
              <div className="grid gap-3 mb-3" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))' }}>
                {data.live.map(lv => <LiveCard key={lv.kiosk_id} live={lv} audience="court" deviceName={lv.device_name} siteName={lv.site_name} testId={`bm-live-${lv.kiosk_id}`} />)}
              </div>
            )}
            <div className="flex gap-2 flex-wrap">
              {data.devices.map(d => (
                <span key={d.kiosk_id} data-testid={`bm-device-${d.kiosk_id}`}
                  style={{ fontSize: 12, color: '#ccc', background: '#141414', border: '1px solid #1E1E1E', borderRadius: 999, padding: '5px 12px', display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ width: 8, height: 8, borderRadius: 999, background: d.status !== 'active' ? GREY : d.is_online ? GREEN : '#FF2D55', boxShadow: d.is_online ? `0 0 6px ${GREEN}` : 'none' }} />
                  {d.name}{d.site_name ? ` · ${d.site_name}` : ''}
                  {/* P2.5: offline is checked BEFORE in use. A phone that died mid-rental
                      keeps its session block until the next beat, and used to read "in use"
                      hours after it had gone quiet. Being unreachable is the louder fact. */}
                  <span style={{ color: GREY }}>{d.status !== 'active' ? 'inactive' : !d.is_online ? `offline · last seen ${fmtAgo(d.last_seen_ms)}` : d.in_session ? 'in use' : 'idle'}</span>
                </span>
              ))}
            </div>
          </div>
        )}

        {error && <p style={{ color: '#FF2D55', marginBottom: 12 }}>{error}</p>}
        {loading && !data && <p style={{ color: GREY, textAlign: 'center', padding: 40 }}>Loading…</p>}

        {data && !data.has_devices && data.rentals.length === 0 && (
          <div style={{ ...card, textAlign: 'center', padding: 50 }} data-testid="bm-no-devices">
            <Smartphone className="h-10 w-10 mx-auto mb-3" style={{ color: '#333' }} />
            <p style={{ color: GREY, fontSize: 14 }}>No rental kiosk is linked to your court. Ask the machine owner to link one.</p>
          </div>
        )}

        {data && (data.has_devices || data.rentals.length > 0) && (
          <>
            {/* Stat tiles */}
            {/* T1: Booked and Played replace "Hours" once the server sends both; before that the tiles read as they did. */}
            <div className={t.booked_minutes != null && t.played_minutes != null ? 'grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-4' : 'grid grid-cols-2 sm:grid-cols-5 gap-3 mb-4'}>
              <Tile label="Rentals" value={t.rentals} sub={t.running ? `${t.running} running now` : null} testId="bm-stat-rentals" />
              {t.booked_minutes != null && t.played_minutes != null ? (
                <>
                  <Tile label="Booked" value={hours(t.booked_minutes)} sub={fmtHrMin(t.booked_minutes)} testId="bm-stat-booked" />
                  <Tile label="Played" value={hours(t.played_minutes)} sub={fmtHrMin(t.played_minutes)} testId="bm-stat-played" />
                </>
              ) : <Tile label="Hours" value={hours(t.minutes)} sub={fmtHrMin(t.minutes)} testId="bm-stat-hours" />}
              <Tile label="Machine takings" value={fmtCentavos(t.customers_paid_centavos)} testId="bm-stat-paid" />
              <Tile label="Your share" value={fmtCentavos(t.court_share_centavos)} color={t.court_share_centavos < 0 ? AMBER : GREEN}
                sub={t.court_share_complete ? 'derived' : 'partial — see settlement'} testId="bm-stat-court" />
              <Tile label="Payable to HighMark" value={fmtCentavos(p.owner_centavos)} color={AMBER}
                sub={`${p.sessions} unsettled rental${p.sessions === 1 ? '' : 's'}${p.since_ms ? ` since ${fmtMs(p.since_ms)}` : ''} · derived`} testId="bm-stat-payable" />
            </div>
            {t.late_stops > 0 && (
              <p style={{ fontSize: 12, color: AMBER, marginBottom: 12 }} data-testid="bm-late-stops">
                {t.late_stops} late stop{t.late_stops === 1 ? '' : 's'} in this view — a problem stop within 15 minutes of the first hour, {fmtCentavos(t.late_stop_forgone_centavos)} not charged. Counted on both sides.
              </p>
            )}

            {((t.voucher_discount_count ?? t.vouchers) > 0 || t.voucher_discount_centavos > 0) && (
              <div style={{ ...card, marginBottom: 16 }} data-testid="bm-vouchers">
                <h2 style={{ fontFamily: 'Oswald, sans-serif', fontSize: 15, fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>Vouchers</h2>
                <div className="flex gap-6 flex-wrap">
                  {/* T1: "Used" counts only the vouchers that took money off; free time has its own card. */}
                  <div><p style={labelStyle}>Used</p><p style={{ fontSize: 18, fontWeight: 700 }} data-testid="bm-voucher-count">{t.voucher_discount_count ?? t.vouchers}</p></div>
                  <div><p style={labelStyle}>Discounts given</p><p style={{ fontSize: 18, fontWeight: 700 }} data-testid="bm-voucher-discount">{fmtCentavos(t.voucher_discount_centavos)}</p></div>
                  <div><p style={labelStyle}>Funded by you</p><p style={{ fontSize: 18, fontWeight: 700, color: AMBER }} data-testid="bm-voucher-court">{fmtCentavos(t.voucher_court_funded_centavos)}</p></div>
                  <div><p style={labelStyle}>Funded by HighMark</p><p style={{ fontSize: 18, fontWeight: 700, color: GREEN }} data-testid="bm-voucher-owner">{fmtCentavos(t.voucher_owner_funded_centavos)}</p></div>
                </div>
                <p style={{ fontSize: 11, color: '#555', marginTop: 6 }}>Already inside &quot;Your share&quot;: a voucher lowers what the customer paid; the part you fund comes out of your side, HighMark&apos;s part out of theirs.</p>
              </div>
            )}

            {/* T1: a bonus voucher takes nothing off the price — it gives machine time — so it stands
                on its own, and a period with only free time never shows a card of ₱0.00 figures. */}
            {t.voucher_bonus_count > 0 && (
              <div style={{ ...card, marginBottom: 16 }} data-testid="bm-vouchers-bonus">
                <h2 style={{ fontFamily: 'Oswald, sans-serif', fontSize: 15, fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>Free time</h2>
                <div><p style={labelStyle}>Free-time vouchers</p><p style={{ fontSize: 18, fontWeight: 700 }} data-testid="bm-voucher-bonus">{t.voucher_bonus_count}<span style={{ fontSize: 13, fontWeight: 400, color: GREY }}> · {fmtHrMin(t.voucher_bonus_minutes || 0)} free</span></p></div>
              </div>
            )}

            {/* Settlement panel */}
            <div style={{ ...card, marginBottom: 16 }} data-testid="bm-settlement">
              <h2 style={{ fontFamily: 'Oswald, sans-serif', fontSize: 15, fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 12 }}>Settlement</h2>
              <div className="flex gap-6 flex-wrap">
                <Billing title="Last settlement" b={data.last_billing} testId="bm-last-billing" emptyText="No settlement yet." onStatement={openStatement} />
                <div style={{ flex: 1, minWidth: 220 }} data-testid="bm-current-payable">
                  <p style={labelStyle}>Payable now</p>
                  <p style={{ fontFamily: 'Oswald, sans-serif', fontSize: 22, fontWeight: 900, color: AMBER, marginTop: 6 }}>{fmtCentavos(data.current_payable.owner_centavos)}</p>
                  <p style={{ fontSize: 12, color: GREY }}>
                    owner&apos;s share of {data.current_payable.sessions} unsettled rental{data.current_payable.sessions === 1 ? '' : 's'}
                    {' · '}customers paid {fmtCentavos(data.current_payable.gross_centavos)}
                    {' · '}court keeps {fmtCentavos(data.current_payable.court_centavos)}
                  </p>
                  <p style={{ fontSize: 11, color: '#555', marginTop: 4 }}>{data.payable_to_owner.note}</p>
                </div>
                <Billing title="Previous settlement" b={data.previous_payable} testId="bm-previous-payable" emptyText="No earlier settlement." onStatement={openStatement} />
              </div>

              {/* 7.0f: the settlements on record, each with its statement — the same document HighMark reads, without their figure. */}
              {data.billings?.length > 0 && (
                <div style={{ marginTop: 16, borderTop: '1px solid #1E1E1E', paddingTop: 10 }} data-testid="bm-billings">
                  <p style={labelStyle}>Statements</p>
                  {data.billings.map(b => (
                    <div key={`${b.source}-${b.id}`} className="flex items-center justify-between gap-3 flex-wrap" data-testid={`bm-billing-${b.source}-${b.id}`}
                      style={{ padding: '7px 0', borderBottom: '1px solid #1E1E1E', fontSize: 13 }}>
                      <span>
                        {dmy(epochDayToYmd(b.period_from_epoch_day))} – {dmy(epochDayToYmd(b.period_to_epoch_day))}
                        <span style={{ color: GREY }}> · {b.session_count ?? '—'} rentals · takings {fmtCentavos(b.gross_centavos)} · yours <span style={{ color: b.court_centavos < 0 ? AMBER : GREEN }}>{fmtCentavos(b.court_centavos)}</span></span>
                        <span style={{ color: '#555' }}> · {b.source === 'web' ? 'on the web' : 'on the machine'}{b.site_name ? ` · ${b.site_name}` : ''}</span>
                        {b.conflict && <span style={{ color: '#FF2D55', fontWeight: 700 }}> · under review</span>}
                      </span>
                      <button onClick={() => openStatement(b)} data-testid={`bm-billing-statement-${b.source}-${b.id}`} style={statementLink}><FileText className="h-3 w-3" /> Statement</button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Rentals */}
            <h2 style={{ fontFamily: 'Oswald, sans-serif', fontSize: 15, fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>Rentals</h2>
            {data.rentals.length === 0 ? (
              <div style={{ ...card, color: GREY, textAlign: 'center' }} data-testid="bm-no-rentals">No rentals in this period.</div>
            ) : (
              <div style={{ ...card, padding: 0, overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 720 }} data-testid="bm-rentals">
                  <thead>
                    <tr style={{ borderBottom: '1px solid #1E1E1E' }}>
                      {['When', 'Machine', 'Duration', 'Ended', 'Charged', 'Court side', 'Flags'].map(h => (
                        <th key={h} style={{ ...labelStyle, textAlign: 'left', padding: '10px 12px', fontWeight: 700 }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {data.rentals.map(r => (
                      <tr key={r.row_id} data-testid={`bm-rental-${r.row_id}`} style={{ borderBottom: '1px solid #1E1E1E' }}>
                        <td style={{ padding: '10px 12px', fontSize: 13 }}>
                          {period !== 'day' && <span style={{ color: GREY }}>{new Date(r.started_at_ms).toLocaleDateString([], { month: 'short', day: 'numeric' })} · </span>}
                          {r.status === 'RUNNING' && <span style={{ color: AMBER, fontWeight: 700 }}>LIVE · </span>}
                          {hhmm(r.started_at_ms)} – {r.status === 'RUNNING' ? 'now' : hhmm(r.ended_at_ms)}
                        </td>
                        <td style={{ padding: '10px 12px', fontSize: 12, color: GREY }}>{r.site_name || r.device_name || '—'}</td>
                        {/* T1: booked and played — "2 hrs", "2 hrs booked · 1 min played", "open · 45 min".
                            Until the server sends both (and for a RUNNING row) the P6 billed duration stays. */}
                        <td style={{ padding: '10px 12px', fontSize: 13 }} data-testid={`bm-rental-time-${r.row_id}`}>
                          {fmtBookedPlayed(r.booked_minutes, r.played_minutes, r.mode)
                            ?? <>{fmtHrMin(r.billed_minutes ?? r.minutes)}<span style={{ color: '#555' }}>{r.mode ? ` · ${r.mode === 'OPEN' ? 'open' : 'fixed'}` : ''}</span></>}
                        </td>
                        <td style={{ padding: '10px 12px', fontSize: 12, color: r.stop_reason ? AMBER : GREY }}>{r.stop_reason ? (STOP_LABEL[r.stop_reason] || r.stop_reason) : (r.status === 'RUNNING' ? 'running' : 'normal')}</td>
                        <td style={{ padding: '10px 12px', fontSize: 13, fontWeight: 600 }}>
                          {r.status === 'RUNNING' ? '—' : fmtCentavos(r.charged_centavos)}
                          {r.voucher && (
                            <div style={{ fontSize: 11, fontWeight: 400, color: r.voucher.consumed ? '#4FC3F7' : GREY }} data-testid={`bm-voucher-line-${r.row_id}`}>
                              {r.voucher.code}{r.voucher.consumed
                                ? ` −${fmtCentavos(r.voucher.discount_centavos)} (you ${fmtCentavos(r.voucher.court_funded_centavos)})`
                                : r.voucher.released ? ' returned' : r.voucher.state === 'CLAIMED' ? ' claimed' : ''}
                            </div>
                          )}
                        </td>
                        <td style={{ padding: '10px 12px', fontSize: 13, fontWeight: 600, color: r.court_side_centavos < 0 ? AMBER : GREEN }}>
                          {r.court_side_derived ? fmtCentavos(r.court_side_centavos) : <span style={{ color: GREY, fontWeight: 400 }}>{r.court_side_note || 'see settlement'}</span>}
                        </td>
                        <td style={{ padding: '10px 12px' }}>
                          <div className="flex gap-1 flex-wrap">
                            {r.flags.map(f => (
                              <span key={f} data-testid={`bm-flag-${f}`} style={{ fontSize: 10, fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.5,
                                color: FLAG_COLOR[f] || GREY, background: (FLAG_COLOR[f] || GREY) + '18', padding: '2px 7px', borderRadius: 999 }}>{FLAG_LABEL[f] || f}</span>
                            ))}
                            {r.collected && <span style={{ fontSize: 10, color: '#555' }}>settled</span>}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <p style={{ fontSize: 11, color: '#555', marginTop: 14, lineHeight: 1.5 }}>{data.derived_note}</p>
          </>
        )}
      </main>
      {statement && <StatementModal title={statement.title} load={statement.load} audience="court" onClose={() => setStatement(null)} />}
    </div>
  );
}
